// src/modules/RaffleAncol/components/RaffleAncolFormModal.tsx
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import ImageDropzone from "components/ImageDropzone";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  storeRaffleAncol,
  updateRaffleAncol,
  getRaffleAncolDetail,
} from "redux/actions/raffleAncolActions";
import moment from "moment";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

interface Props {
  raffleId?: string | null;
  onSuccess?: () => void;
}

const initialValues = {
  rewardName: "",
  description: "",
  qty: "",
  activePerieodeStart: "",
  activePerieodeEnd: "",
  isMultipleDraw: "0",
};

const RaffleAncolFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  raffleId,
  onSuccess,
}) => {
  const isAddMode = !raffleId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;
  const [filesRewardImage, setFilesRewardImage] = useState<any>([]);

  const { isFormLoading, raffleDetail } = useSelector(
    (state: any) => state.raffleAncolReducer
  );
  const { appUrl } = useSelector((state: any) => state.globalReducer);

  // Close modal helper
  const handleCloseModal = () => {
    onClose && onClose();
    setFilesRewardImage([]);
    // reset formik if needed
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  // Submit handler
  const handleSubmit = (values: any) => {
    const {
      rewardName,
      description,
      qty,
      activePerieodeStart,
      activePerieodeEnd,
      isMultipleDraw,
    } = values;

    // If file is an object and not an existing server file, send it.
    const rewardImage =
      filesRewardImage &&
      filesRewardImage.length > 0 &&
      typeof filesRewardImage[0] === "object" &&
      !filesRewardImage[0].existing
        ? filesRewardImage[0]
        : null;

    const payload = {
      rewardName,
      description,
      qty,
      activePerieodeStart,
      activePerieodeEnd,
      rewardImage,
      isMultipleDraw,
    };

    const onSuccessCallback = () => {
      handleCloseModal();
      onSuccess && onSuccess();
    };

    if (isAddMode) {
      dispatch(storeRaffleAncol(payload, onSuccessCallback) as any);
    } else {
      dispatch(updateRaffleAncol(raffleId as string, payload, onSuccessCallback) as any);
    }
  };

  // Prefill form values when editing (use formikRef)
  useEffect(() => {
  if (!isAddMode && raffleDetail?.raffle && formikRef.current) {
    const raffle = raffleDetail.raffle;

    formikRef.current.setFieldValue("rewardName", raffle.reward_name || "");
    formikRef.current.setFieldValue("qty", raffle.qty?.toString() ?? "");
    formikRef.current.setFieldValue("description", raffle.description || "");
    formikRef.current.setFieldValue(
      "activePerieodeStart",
      raffle.active_periode_start
        ? moment(raffle.active_periode_start).format("YYYY-MM-DD")
        : ""
    );
    formikRef.current.setFieldValue(
      "activePerieodeEnd",
      raffle.active_periode_end
        ? moment(raffle.active_periode_end).format("YYYY-MM-DD")
        : ""
    );

    formikRef.current.setFieldValue(
      "isMultipleDraw",
      raffle.is_multiple_draw?.toString() ?? "0"
    );

    // ✅ Perbaiki preview Dropzone (biar gak error undefined)
    if (raffle.img_url) {
      const previewUrl =
        raffle.img_url.startsWith("http") ||
        (appUrl && raffle.img_url.startsWith("/"))
          ? raffle.img_url
          : `${appUrl || process.env.REACT_APP_API_URL}/storage/${raffle.img_url}`;

      setFilesRewardImage([
        {
          name: raffle.reward_name || "reward_image",
          preview: previewUrl,
          existing: true, // tandai bahwa ini file dari server
        },
      ]);
    } else {
      setFilesRewardImage([]);
    }
  }

  // ✅ Kalau mode Add, reset form
  if (isAddMode && formikRef.current) {
    formikRef.current.resetForm();
    setFilesRewardImage([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [raffleDetail, isAddMode]);


  // Fetch detail on open/edit
  useEffect(() => {
    if (!isAddMode && raffleId) {
      dispatch(getRaffleAncolDetail(raffleId) as any);
    }
  }, [raffleId, isAddMode, dispatch]);

  const validationSchema = yup.object({
    rewardName: yup.string().required("Reward Name is required"),
    qty: yup.number().required("Qty is required"),
    description: yup.string().required("Description is required"),
    activePerieodeStart: yup
      .string()
      .required("Active Period Start is required"),
    activePerieodeEnd: yup.string().required("Active Period End is required"),
    isMultipleDraw: yup.string().required("Draw Type is required"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxHeight={"95vh"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {isAddMode ? "Add Raffle Reward" : "Update Raffle Reward"}
              </Text>
            </DialogHeader>

            <DialogContent
              style={{
                maxHeight: "calc(95vh - 200px)",
                overflowY: "auto",
                paddingRight: 15,
              }}
            >
              <Stack spacing="xl" style={{ paddingRight: 10 }}>
                {/* Reward Name */}
                <div style={{ marginBottom: 20 }}>
                  <Field name="rewardName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="rewardName">Reward Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            id="rewardName"
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </div>

              {/* Active Period (Start & End in one row) */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: 20 }}>
                {/* Active Period Start */}
                <Field name="activePerieodeStart">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="activePerieodeStart">Active Period Start</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="date"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                {/* Active Period End */}
                <Field name="activePerieodeEnd">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="activePerieodeEnd">Active Period End</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="date"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: 20,
                }}
              >
                {/* Reward Quantity */}
                <Field name="qty">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="qty">Reward Quantity</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          id="qty"
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">
                          {meta.error}
                        </InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                {/* Draw Type */}
                <Field name="isMultipleDraw">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="isMultipleDraw">Draw Type</FormLabel>
                      <InputGroup inputSize={40}>
                        <select
                          {...field}
                          id="isMultipleDraw"
                          style={{
                            width: "100%",
                            height: 40,
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            padding: "0 12px",
                          }}
                        >
                          <option value="0">Single Draw</option>
                          <option value="1">Multiple Draw</option>
                        </select>
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">
                          {meta.error}
                        </InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>
              </div>  
              {/* Description (CKEditor) */}
              <div style={{ marginBottom: 20 }}>
                <Stack spacing="xs">
                  <FormLabel htmlFor="description">Description</FormLabel>
                  <CKEditor
                    editor={ClassicEditor}
                    data={values.description}
                    onChange={(event: any, editor: any) => {
                      const data = editor.getData();
                      setFieldValue("description", data);
                    }}
                    config={{
                      toolbar: [
                        "heading",
                        "|",
                        "bold",
                        "italic",
                        "link",
                        "bulletedList",
                        "numberedList",
                        "|",
                        "blockQuote",
                        "insertTable",
                        "|",
                        "undo",
                        "redo",
                      ],
                    }}
                  />
                </Stack>
              </div>

              {/* Reward Image (Dropzone global - do not change) */}
              <Stack spacing="xs">
                <FormLabel>Reward Image</FormLabel>
                <ImageDropzone
                  name="reward_image"
                  files={filesRewardImage}
                  setFiles={setFilesRewardImage}
                />
              </Stack>
            </Stack>
            </DialogContent>

            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button block variant="outline" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isFormLoading}
                  block
                  variant="primary"
                >
                  {isAddMode ? "Add" : "Update"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default RaffleAncolFormModal;
